import { useState, useEffect } from 'react';
import { History, RefreshCw, AlertTriangle, ArrowLeft, ShoppingCart, Eye, ExternalLink, Inbox } from 'lucide-react';
import { plvApi, formatDate, getStatusInfo } from '../api/plvApi';
import { formatPrice } from '../utils/pricingUtils';
import type { PLVOrder } from '../types/plv.types';
import { useAuthStore } from '../../../stores/auth-store';

interface OrderHistoryProps {
  onViewOrder: (orderId: number) => void;
  onBackToCatalog: () => void;
}

export function OrderHistory({ onViewOrder, onBackToCatalog }: OrderHistoryProps) {
  const [orders, setOrders] = useState<PLVOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuthStore();
  const societyId = currentUser?.societyId;

  useEffect(() => {
    if (societyId) loadOrders();
  }, [societyId]);

  const loadOrders = async () => {
    if (!societyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await plvApi.getOrders(societyId);
      const sortedOrders = data.sort(
        (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime(),
      );
      setOrders(sortedOrders);
    } catch {
      setError('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-affinicar-secondary tw-border-t-transparent tw-rounded-full tw-animate-spin" />
        <p className="tw-mt-3 tw-text-gray-500">Chargement de l'historique...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <AlertTriangle className="tw-w-12 tw-h-12 tw-text-red-400 tw-mb-3" />
        <h4 className="tw-text-lg tw-font-medium tw-text-red-600">Erreur de chargement</h4>
        <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-4">{error}</p>
        <div className="tw-flex tw-gap-2">
          <button
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
            onClick={loadOrders}
          >
            <RefreshCw className="tw-w-4 tw-h-4" />
            Réessayer
          </button>
          <button
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
            onClick={onBackToCatalog}
          >
            <ArrowLeft className="tw-w-4 tw-h-4" />
            Retour au catalogue
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-400">
        <Inbox className="tw-w-16 tw-h-16 tw-mb-3" />
        <h4 className="tw-text-lg tw-font-medium tw-text-gray-500">Aucune commande</h4>
        <p className="tw-text-sm tw-mt-1 tw-mb-4">Vous n'avez pas encore passé de commande PLV.</p>
        <button
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
          onClick={onBackToCatalog}
        >
          <ShoppingCart className="tw-w-4 tw-h-4" />
          Commencer mes achats
        </button>
      </div>
    );
  }

  return (
    <div className="tw-space-y-4">
      {/* Header */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center tw-gap-3">
          <div>
            <h4 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
              <History className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
              Historique des commandes
            </h4>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
              {orders.length} commande{orders.length > 1 ? 's' : ''} au total
            </p>
          </div>
          <div className="tw-flex tw-gap-2">
            <button
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={loadOrders}
            >
              <RefreshCw className="tw-w-4 tw-h-4" />
              Actualiser
            </button>
            <button
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={onBackToCatalog}
            >
              <ArrowLeft className="tw-w-4 tw-h-4" />
              Catalogue
            </button>
          </div>
        </div>
      </div>

      {/* Orders list */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-divide-y tw-divide-gray-100">
        {orders.map(order => {
          const statusInfo = getStatusInfo(order.status);
          const totalItems = order.items?.length || 0;
          const totalHT =
            order.items?.reduce((sum, item) => sum + (Number(item.unitPriceHt) || 0), 0) || 0;

          return (
            <div key={order.id} className="tw-p-4">
              {/* Desktop */}
              <div className="tw-hidden md:tw-flex tw-items-center tw-gap-4">
                <div className="tw-w-28 tw-text-center">
                  <div className="tw-font-bold">{order.orderNumber}</div>
                  <span className="tw-text-xs tw-text-gray-500">Commande</span>
                </div>

                <div className="tw-flex-1">
                  <div className="tw-font-medium tw-text-sm">{formatDate(order.creationDate)}</div>
                  <span className="tw-text-xs tw-text-gray-500">
                    {totalItems} article{totalItems > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="tw-w-32">
                  <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full ${statusInfo.colorClass}`}>
                    {statusInfo.label}
                  </span>
                </div>

                <div className="tw-w-28">
                  <div className="tw-font-bold tw-text-sm">{formatPrice(totalHT)}</div>
                  <span className="tw-text-xs tw-text-gray-500">Total HT</span>
                </div>

                <div className="tw-w-32">
                  {order.trackingNumber && (
                    <div>
                      <span className="tw-text-xs tw-text-gray-500 tw-block">Suivi :</span>
                      {order.trackingUrl ? (
                        <a
                          href={order.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tw-text-xs tw-text-affinicar-secondary hover:tw-underline tw-inline-flex tw-items-center tw-gap-1"
                        >
                          {order.trackingNumber}
                          <ExternalLink className="tw-w-3 tw-h-3" />
                        </a>
                      ) : (
                        <span className="tw-text-xs">{order.trackingNumber}</span>
                      )}
                    </div>
                  )}
                </div>

                <button
                  className="tw-p-2 tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-affinicar-secondary tw-transition-colors"
                  onClick={() => onViewOrder(order.id)}
                  title="Voir les détails"
                >
                  <Eye className="tw-w-4 tw-h-4" />
                </button>
              </div>

              {/* Mobile */}
              <div className="md:tw-hidden tw-space-y-2">
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div>
                    <div className="tw-font-bold">{order.orderNumber}</div>
                    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full tw-mt-1 ${statusInfo.colorClass}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-text-right">
                      <div className="tw-font-bold tw-text-sm">{formatPrice(totalHT)}</div>
                      <span className="tw-text-xs tw-text-gray-500">HT</span>
                    </div>
                    <button
                      className="tw-p-2 tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-500 hover:tw-bg-gray-50"
                      onClick={() => onViewOrder(order.id)}
                    >
                      <Eye className="tw-w-4 tw-h-4" />
                    </button>
                  </div>
                </div>
                <div className="tw-flex tw-justify-between tw-items-center tw-text-xs tw-text-gray-500">
                  <span>
                    {formatDate(order.creationDate)} — {totalItems} article{totalItems > 1 ? 's' : ''}
                  </span>
                  {order.trackingNumber && (
                    <span>
                      Suivi :{' '}
                      {order.trackingUrl ? (
                        <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="tw-text-affinicar-secondary hover:tw-underline tw-inline-flex tw-items-center tw-gap-0.5">
                          {order.trackingNumber}
                          <ExternalLink className="tw-w-3 tw-h-3" />
                        </a>
                      ) : (
                        order.trackingNumber
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
